import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { useTheme, adminTheme } from '@/context/ThemeContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Search, MoreVertical, Eye, Mail, Phone, MessageSquare, Filter, Clock, Globe, Rocket } from 'lucide-react';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

export default function Enquiries() {
  const { isDark } = useTheme();
  const [enquiries, setEnquiries] = useState([]);
  const [filteredEnquiries, setFilteredEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [selectedEnquiry, setSelectedEnquiry] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  useEffect(() => {
    fetchEnquiries();
  }, []);

  useEffect(() => {
    filterEnquiries();
  }, [enquiries, searchTerm, statusFilter, sourceFilter]);

  const fetchEnquiries = async () => {
    try {
      const response = await axios.get(`${API}/enquiries`);
      setEnquiries(response.data);
    } catch (error) {
      console.error('Error fetching enquiries:', error);
      toast.error('Failed to fetch enquiries');
    } finally {
      setLoading(false);
    }
  };

  const filterEnquiries = () => {
    let filtered = [...enquiries];

    if (searchTerm) {
      filtered = filtered.filter(
        (enq) =>
          enq.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          enq.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
          enq.email.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== 'all') {
      filtered = filtered.filter((enq) => enq.status === statusFilter);
    }

    if (sourceFilter !== 'all') {
      filtered = filtered.filter((enq) => enq.source === sourceFilter);
    }

    setFilteredEnquiries(filtered);
  };

  const updateEnquiryStatus = async (enqId, newStatus) => {
    try {
      await axios.put(`${API}/enquiries/${enqId}`, { status: newStatus });
      setEnquiries((prev) =>
        prev.map((enq) => (enq.id === enqId ? { ...enq, status: newStatus } : enq))
      );
      toast.success('Enquiry status updated');
    } catch (error) {
      console.error('Error updating enquiry:', error);
      toast.error('Failed to update status');
    }
  };

  const deleteEnquiry = async (enqId) => {
    try {
      await axios.delete(`${API}/enquiries/${enqId}`);
      setEnquiries((prev) => prev.filter((enq) => enq.id !== enqId));
      toast.success('Enquiry deleted');
    } catch (error) {
      console.error('Error deleting enquiry:', error);
      toast.error('Failed to delete enquiry');
    }
  };

  const statusColors = {
    new: 'bg-blue-100 text-blue-700 border-blue-200',
    in_progress: 'bg-yellow-100 text-yellow-700 border-yellow-200',
    resolved: 'bg-green-100 text-green-700 border-green-200',
  };

  const sourceColors = {
    'aurousorbit.ai': 'bg-purple-100 text-purple-700 border-purple-300',
    'aurousconsultancy.com': 'bg-blue-100 text-blue-700 border-blue-300',
  };

  const getSourceIcon = (source) => {
    if (source === 'aurousorbit.ai') {
      return <Rocket className="w-3 h-3" />;
    }
    return <Globe className="w-3 h-3" />;
  };

  const statuses = ['new', 'in_progress', 'resolved'];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="enquiries-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className={cn("text-2xl font-bold", adminTheme.textPrimary(isDark))}>Enquiries</h1>
          <p className={adminTheme.textSecondary(isDark)}>Manage customer enquiries and requests</p>
        </div>
        <Badge variant="outline" className={cn("self-start sm:self-center px-4 py-2", isDark ? "border-slate-600 text-slate-300" : "")}>
          {filteredEnquiries.length} Enquiries
        </Badge>
      </div>

      {/* Filters */}
      <Card className={cn("border-0 shadow-md", adminTheme.cardBg(isDark))}>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className={cn("absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4", adminTheme.textMuted(isDark))} />
              <Input
                placeholder="Search by name, subject, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={cn("pl-10", isDark ? "bg-slate-800 border-slate-700 text-white placeholder:text-slate-500" : "")}
                data-testid="search-enquiries"
              />
            </div>
            <Select value={sourceFilter} onValueChange={setSourceFilter}>
              <SelectTrigger className={cn("w-full sm:w-48", isDark ? "bg-slate-800 border-slate-700 text-white" : "")} data-testid="enquiry-source-filter">
                <Globe className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by source" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Sources</SelectItem>
                <SelectItem value="aurousconsultancy.com">
                  <span className="flex items-center gap-2">
                    <Globe className="w-3 h-3" />
                    Website
                  </span>
                </SelectItem>
                <SelectItem value="aurousorbit.ai">
                  <span className="flex items-center gap-2">
                    <Rocket className="w-3 h-3" />
                    AurousOrbit.ai
                  </span>
                </SelectItem>
              </SelectContent>
            </Select>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className={cn("w-full sm:w-48", isDark ? "bg-slate-800 border-slate-700 text-white" : "")} data-testid="enquiry-status-filter">
                <Filter className="w-4 h-4 mr-2" />
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {statuses.map((status) => (
                  <SelectItem key={status} value={status} className="capitalize">
                    {status.replace('_', ' ')}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Enquiries Table */}
      <Card className={cn("border-0 shadow-md overflow-hidden", adminTheme.cardBg(isDark))}>
        <CardContent className="p-0">
          <div className="responsive-table">
            <Table>
              <TableHeader>
                <TableRow className={isDark ? "bg-slate-800/80 border-slate-700" : "bg-slate-100 border-slate-200"}>
                  <TableHead className={adminTheme.textSecondary(isDark)}>Contact</TableHead>
                  <TableHead className={adminTheme.textSecondary(isDark)}>Source</TableHead>
                  <TableHead className={adminTheme.textSecondary(isDark)}>Subject</TableHead>
                  <TableHead className={adminTheme.textSecondary(isDark)}>Message</TableHead>
                  <TableHead className={adminTheme.textSecondary(isDark)}>Date</TableHead>
                  <TableHead className={adminTheme.textSecondary(isDark)}>Status</TableHead>
                  <TableHead className={cn("text-right", adminTheme.textSecondary(isDark))}>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredEnquiries.map((enq) => (
                  <TableRow key={enq.id} className={cn(
                    "transition-colors border-b",
                    isDark 
                      ? "border-slate-800 hover:bg-slate-800/30" 
                      : "border-slate-100 hover:bg-slate-50"
                  )}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white font-medium">
                          {enq.name.charAt(0)}
                        </div>
                        <div>
                          <p className={cn("font-medium", adminTheme.textPrimary(isDark))}>{enq.name}</p>
                          <p className={cn("text-sm", adminTheme.textMuted(isDark))}>{enq.email}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge 
                        variant="outline" 
                        className={cn(
                          "flex items-center gap-1 text-xs font-medium",
                          sourceColors[enq.source] || (isDark ? 'bg-slate-700 text-slate-300 border-slate-600' : 'bg-slate-100 text-slate-700 border-slate-300')
                        )}
                      >
                        {getSourceIcon(enq.source)}
                        {enq.source === 'aurousorbit.ai' ? 'AurousOrbit' : 'Website'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <p className={cn("font-medium max-w-xs truncate", adminTheme.textPrimary(isDark))}>{enq.subject}</p>
                    </TableCell>
                    <TableCell>
                      <p className={cn("text-sm max-w-xs truncate", adminTheme.textMuted(isDark))}>{enq.message}</p>
                    </TableCell>
                    <TableCell>
                      <div className={cn("flex items-center gap-2 text-sm", adminTheme.textMuted(isDark))}>
                        <Clock className="w-4 h-4" />
                        {new Date(enq.created_at).toLocaleDateString()}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={enq.status}
                        onValueChange={(value) => updateEnquiryStatus(enq.id, value)}
                      >
                        <SelectTrigger
                          className={cn('w-32 h-8 text-xs capitalize border', statusColors[enq.status])}
                        >
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {statuses.map((status) => (
                            <SelectItem key={status} value={status} className="capitalize">
                              {status.replace('_', ' ')}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className={adminTheme.textSecondary(isDark)} data-testid={`enquiry-actions-${enq.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedEnquiry(enq);
                              setViewDialogOpen(true);
                            }}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            View Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => window.open(`mailto:${enq.email}`)}>
                            <Mail className="w-4 h-4 mr-2" />
                            Send Email
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => deleteEnquiry(enq.id)} className="text-red-600">
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredEnquiries.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-12">
                      <div className="flex flex-col items-center gap-3">
                        <div className={cn("p-4 rounded-full", isDark ? "bg-slate-800" : "bg-slate-100")}>
                          <MessageSquare className={adminTheme.textMuted(isDark)} />
                        </div>
                        <p className={adminTheme.textSecondary(isDark)}>No enquiries found</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* View Enquiry Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Enquiry Details</DialogTitle>
            <DialogDescription>Review the enquiry information</DialogDescription>
          </DialogHeader>
          {selectedEnquiry && (
            <div className="space-y-6">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white text-2xl font-medium">
                  {selectedEnquiry.name.charAt(0)}
                </div>
                <div>
                  <h3 className="text-xl font-semibold">{selectedEnquiry.name}</h3>
                  <Badge className={cn('capitalize mt-1', statusColors[selectedEnquiry.status])}>
                    {selectedEnquiry.status.replace('_', ' ')}
                  </Badge>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <p className="text-sm text-slate-500">Email</p>
                  <p className="font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    {selectedEnquiry.email}
                  </p>
                </div>
                {selectedEnquiry.phone && (
                  <div className="space-y-1">
                    <p className="text-sm text-slate-500">Phone</p>
                    <p className="font-medium flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      {selectedEnquiry.phone}
                    </p>
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <p className="text-sm text-slate-500">Subject</p>
                <p className="font-medium">{selectedEnquiry.subject}</p>
              </div>

              <div className="space-y-2">
                <p className="text-sm text-slate-500">Message</p>
                <p className="text-sm bg-slate-50 p-4 rounded-lg">{selectedEnquiry.message}</p>
              </div>

              <div className="flex gap-2">
                <Button className="flex-1" onClick={() => window.open(`mailto:${selectedEnquiry.email}`)}>
                  <Mail className="w-4 h-4 mr-2" />
                  Reply via Email
                </Button>
                <Button variant="outline" onClick={() => setViewDialogOpen(false)}>
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
